import { useCallback, useEffect, useRef, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import { contactBookStore, whenContactBookSettled } from "./contactBookStore";
import { isBookBusy, isPooledBookMode, ownBookKey, toContactBook } from "./contactBookRules";
import type { ContactBook } from "./contactBookRules";
import type { ContactBookMode } from "../../@types/sharedSchema";

/**
 * WHICH BOOK THE SIGNED-IN USER'S CONTACTS LIVE IN — one fetch per session, shared.
 *
 * ```tsx
 * const { bookKey, mode, isPooled } = useContactBook();
 * const document = useContactDocument(bookKey, contactId);   // waits while bookKey is null
 * ```
 *
 * ── Why every book reader goes through this ────────────────────────────────────────
 *
 * The document store, the signal listeners and the write hook are keyed by BOOK, and until
 * the pooled book existed the book was the signed-in email. A member of a pooled agency
 * (the server's plan D4) has their contacts under `org:<orgId>` instead: a card keyed on
 * their email would ask the server for a document under a book with nothing in it, listen
 * on a signal path nothing writes to, and treat every one of their own rows as a
 * colleague's. The server knows which it is (`GET /contacts/book`, zero I/O there), so the
 * dashboard asks once and every reader keys off the answer.
 *
 * ── Two keys, and when to use which ────────────────────────────────────────────────
 *
 *   `bookKey`     STRICT — the server's answer, null while unknown. For an ADDRESS that
 *                 must be right: the document store, a signal listener. A reader given
 *                 null waits (`useContactDocument` renders "resolving"), which costs a
 *                 pooled member one round trip on a cold browser and a personal realtor
 *                 nothing after the first session (the answer is persisted).
 *   `ownBookKey`  PLAUSIBLE — the answer when it is in, the email until then. For a
 *                 COMPARISON ("is this row mine", "is this the book the response named"),
 *                 where the email is the right answer for every personal book and a
 *                 one-frame placeholder for a pooled member.
 *
 * ── `'firestore'` mode never sees a pooled key ─────────────────────────────────────
 *
 * The Firestore listeners (`useLiveContact`'s listener, `useContactByPhone`'s, the old
 * page's `updatedTime` feed) keep addressing `dashboard-users-v2/{email}` and are NOT
 * re-keyed here: a pooled record has no Firestore document at all, so there is nothing
 * under either path for them to find. The runbook requires `CONTACT_DOCUMENT_SOURCE =
 * 'server'` before any agency is flipped, which is what makes "the listener path never
 * meets an `org:` key" a deploy-order fact rather than a hope — and why the server's
 * preview checklist says so too.
 *
 * ── Freshness ──────────────────────────────────────────────────────────────────────
 *
 * Re-asked in the background once the answer is older than the store's stale window, and
 * at once after `invalidateContactBook()` — the pool card calls it after a flip or a
 * reversal, so the screen the admin is on re-keys without a reload; other tabs catch up on
 * their next mount or the stale window. The store belongs to one account and empties
 * itself on a switch.
 */

export type ContactBookState = {
  /** The server's answer, or null while unknown. */
  book: ContactBook | null;
  /** STRICT — see the header. */
  bookKey: string | null;
  /** PLAUSIBLE — see the header. Null only while signed out. */
  ownBookKey: string | null;
  /** `'personal'` while unknown — the narrow answer, which is also the fleet-wide one. */
  mode: ContactBookMode;
  orgId: string | null;
  /** `shared`, `migrating` or `reversing`. */
  isPooled: boolean;
  /** `migrating` or `reversing`: writes are refused server-side right now. */
  isBusy: boolean;
  /** A server answer is held. */
  known: boolean;
  /** Nothing held and the answer is on its way. */
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

type FetchBook = () => Promise<unknown>;

/** The latest `contactsBook` — `useWebotServer()` rebuilds its functions on every render. */
let fetchBook: FetchBook | null = null;

/**
 * Ask the server, once, for whoever is bound. A no-op while a request is out; the answer
 * (or the failure) reaches every mounted reader through the store.
 */
export const fetchContactBook = async (): Promise<void> => {
  if (!contactBookStore.account() || !contactBookStore.claim()) return;
  // Captured when the request LEAVES: the token it travels with is the account's that is
  // signed in now, so this is the session its answer belongs to.
  const session = contactBookStore.session();
  if (!fetchBook) {
    contactBookStore.release(session, new Error("useContactBook: no API client bound"));
    return;
  }
  try {
    const book = toContactBook(await fetchBook());
    if (!book) throw new Error("useContactBook: the server's answer carried no book");
    contactBookStore.record(session, book);
  } catch (error) {
    // Released with the error, never recorded as "personal": that is the one answer that
    // would key a pooled member's cards under a book with no documents in it.
    contactBookStore.release(session, error as Error);
  }
};

/**
 * The book changed under this browser — a flip or a reversal ran. Drops the held answer
 * (and its persisted copy) and asks again at once; every mounted reader re-keys on the
 * answer. Exported for the pool card; nothing else should need it.
 */
export const invalidateContactBook = (): void => {
  contactBookStore.invalidate();
  void fetchContactBook();
};

/**
 * The book, for a reader that is NOT a hook and whose address must be right — the documents
 * tab's Firestore layer. Waits for the answer instead of reading the store's current value
 * (`whenContactBookSettled`); null when there is none to be had. A component wants
 * `useContactBook()`.
 */
export const resolveContactBook = (): Promise<ContactBook | null> =>
  whenContactBookSettled(contactBookStore, () => void fetchContactBook());

export default function useContactBook(): ContactBookState {
  const { user } = useAuth();
  const account = (user?.email as string | undefined) ?? null;
  // During render, and idempotent: the store must belong to THIS account before anything
  // below reads it, or the first frame after a sign-in keys the previous user's book.
  contactBookStore.bindAccount(account);

  const { contactsBook } = useWebotServer();
  const fetcher = useRef(contactsBook);
  fetcher.current = contactsBook;
  fetchBook = () => fetcher.current();

  // Re-render when the answer lands — a subscriber set plus a counter, as in the sibling
  // hooks: this app is on React 17, which has no `useSyncExternalStore`.
  const [, setVersion] = useState(0);
  useEffect(() => contactBookStore.subscribe(() => setVersion((value) => value + 1)), []);

  // Mount, an account switch, an invalidation, the stale window: ask when the store says so.
  const epoch = contactBookStore.epoch();
  useEffect(() => {
    if (contactBookStore.shouldFetch()) void fetchContactBook();
  }, [account, epoch]);

  const refresh = useCallback(async () => {
    contactBookStore.invalidate();
    await fetchContactBook();
  }, []);

  const book = account ? contactBookStore.read() : null;
  const mode: ContactBookMode = book?.mode ?? "personal";
  const inFlight = contactBookStore.isInFlight();
  const error = contactBookStore.error();
  return {
    book,
    bookKey: book?.bookKey ?? null,
    ownBookKey: ownBookKey(book, account),
    mode,
    orgId: book?.orgId ?? null,
    isPooled: isPooledBookMode(mode),
    isBusy: isBookBusy(mode),
    known: book !== null,
    // "About to ask" counts as loading too, so the render before the effect runs does not
    // report a settled "unknown" to a reader that would then paint the narrow answer. A
    // FAILED ask is settled, though: nothing is on its way until the next prompt.
    loading: Boolean(account) && book === null && (inFlight || (!error && contactBookStore.shouldFetch())),
    error,
    refresh
  };
}
